import { APP_I18N } from '@/client/confs/app';
import * as dom from '@/client/utils/dom';
import { SupportedLanguage } from '@/libs/sdk/api';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

// save key
export const LocaleSaveKey = 'app-locale';

export type ILocale = { name: string; lang: string };
export type TDirection = 'ltr' | 'rtl';
export interface ILocaleState {
  locale: string;
  direction: TDirection;
  setDirection: (newDirection: TDirection) => void;
  setLocale: (newLocale: string) => void;
}

// Locales that read right-to-left. A locale switch always derives direction
// from this set (see setLocale below) instead of leaving it to the caller,
// so adding a future RTL locale (e.g. he, fa, ur) only means adding it here.
export const RTL_LOCALES = new Set(['ar']);
export const isRtlLocale = (lang: string): TDirection => (RTL_LOCALES.has(lang) ? 'rtl' : 'ltr');

// store
export const useLocaleStore = createWithEqualityFn<ILocaleState>()(
  persist(
    (set, get) => ({
      locale: '',
      direction: 'ltr',
      setDirection(newDirection: TDirection) {
        const { direction } = get();
        if (direction !== newDirection) set({ direction: newDirection });
      },
      setLocale(newLocale: string) {
        const { locale } = get();
        const result = locale !== newLocale ? APP_I18N.locales.find(item => item.lang === newLocale) : null;
        if (result) set({ locale: result.lang, direction: isRtlLocale(result.lang) });
      },
    }),
    { name: LocaleSaveKey }
  ),
  shallow
);

export type useLocaleProps = Record<string, never>;

const useLocale = () => {
  const { locale, setLocale: storeSetLocale, direction, setDirection } = useLocaleStore(state => state);
  const { i18n, ready } = useTranslation();

  useEffect(() => {
    // Default to English on first visit regardless of browser language
    // (client-confirmed: consistent English landing). Users switch manually.
    if (!locale) storeSetLocale(APP_I18N.defaultLocale);
  }, [locale, storeSetLocale]);

  useEffect(() => {
    if (ready && i18n.language !== locale) i18n.changeLanguage(locale);
    dom.setLocale(locale, direction);
  }, [ready, i18n, locale, direction]);

  return {
    locale: locale as SupportedLanguage,
    locales: APP_I18N.locales,
    defaultLocale: APP_I18N.defaultLocale,
    switchLocale: storeSetLocale,
    switchDirection: setDirection,
  };
};

export default useLocale;
